import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import styles from './ExpandableText.module.scss'

const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export const ExpandableText = ({ text, maxLines = 3, minWidth = 'auto' }) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const [showToggle, setShowToggle] = useState(false)
  const [height, setHeight] = useState('auto')
  const containerRef = useRef(null)
  const contentRef = useRef(null)
  const [isInitialized, setIsInitialized] = useState(false)
  const textIdRef = useRef(text)

  const measurementCacheRef = useRef({ 
    lineHeight: null, 
    lastText: null, 
    cachedFullHeight: null, 
    cachedCollapsedHeight: null 
  })

  const measureHeights = useCallback((forceReset = false) => {
    if (!contentRef.current || !containerRef.current) return { collapsedHeight: 0, fullHeight: 0 }
    
    const trimmedText = text?.trim() || ''
    if (!trimmedText) {
      setShowToggle(false)
      setHeight('auto')
      return { collapsedHeight: 0, fullHeight: 0 }
    }

    const content = contentRef.current
    const cache = measurementCacheRef.current
    
    const shouldRecalculate = !cache.lineHeight || cache.lastText !== text || forceReset
    
    let lineHeight = cache.lineHeight
    let collapsedHeight = cache.cachedCollapsedHeight
    let fullHeight = cache.cachedFullHeight
    
    if (shouldRecalculate) {
      const computedStyle = window.getComputedStyle(content)
      lineHeight = parseInt(computedStyle.lineHeight) || parseInt(computedStyle.fontSize) * 1.2
      
      collapsedHeight = Math.ceil(lineHeight * maxLines)
      
      fullHeight = content.scrollHeight
      
      cache.lineHeight = lineHeight
      cache.lastText = text
      cache.cachedCollapsedHeight = collapsedHeight
      cache.cachedFullHeight = fullHeight
    }
    
    const needsToggle = fullHeight > collapsedHeight
    setShowToggle(needsToggle)
    
    if (needsToggle && (!isInitialized || forceReset)) {
      setHeight(`${collapsedHeight}px`)
      setIsInitialized(true)
    } else if (!needsToggle) {
      setHeight('auto')
    }
    
    return { collapsedHeight: collapsedHeight || 0, fullHeight: fullHeight || 0 }
  }, [maxLines, isInitialized, text])

  useEffect(() => {
    /* istanbul ignore next -- ref comparison timing in jsdom */
    if (textIdRef.current !== text) {
      textIdRef.current = text
      setExpanded(false)
      setIsInitialized(false)
      setHeight('auto')
      setTimeout(() => measureHeights(), 0)
    }
  }, [text, measureHeights])
  
  /* eslint-disable react-hooks/refs -- debounce wraps the callback without accessing refs during render */
  const debouncedMeasureHeights = useMemo(
    () => debounce(measureHeights, 150),
    [measureHeights]
  )
  /* eslint-enable react-hooks/refs */

  useEffect(() => {
    if (!contentRef.current) return

    measureHeights()

    let resizeObserver
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(debouncedMeasureHeights)
      resizeObserver.observe(contentRef.current)
    } else {
      window.addEventListener('resize', debouncedMeasureHeights)
    }

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect()
      } else {
        window.removeEventListener('resize', debouncedMeasureHeights)
      }
    }
  }, [measureHeights, debouncedMeasureHeights])

  const handleToggle = useCallback((e) => {
    e.stopPropagation()
    
    if (!containerRef.current || !contentRef.current) return
    
    const cache = measurementCacheRef.current
    
    let collapsedHeight = cache.cachedCollapsedHeight
    let fullHeight = cache.cachedFullHeight
    
    /* istanbul ignore next -- cache miss fallback */
    if (!collapsedHeight || !fullHeight) {
      const measurements = measureHeights()
      collapsedHeight = measurements.collapsedHeight
      fullHeight = measurements.fullHeight
    }
    
    if (expanded) {
      setHeight(`${fullHeight}px`)
      
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setHeight(`${collapsedHeight}px`)
        })
      })
    } else {
      requestAnimationFrame(() => {
        setHeight(`${fullHeight}px`)
      })
    }
    
    setExpanded(!expanded)
  }, [expanded, measureHeights])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const handleTransitionEnd = () => {
      if (expanded && height !== 'auto') {
        setHeight('auto')
      }
    }

    container.addEventListener('transitionend', handleTransitionEnd)
    return () => container.removeEventListener('transitionend', handleTransitionEnd)
  }, [expanded, height])

  return (
    <div style={{ minWidth }}>
      <div
        ref={containerRef}
        className={`${styles.container} ${!expanded ? styles.collapsed : ''}`}
        style={{ height }}
        tabIndex={expanded ? 0 : -1}
        role={expanded ? 'region' : undefined}
        aria-label={expanded ? t('common.expanded') : undefined}
      >
        <div ref={contentRef} className={styles.content}>
          {text}
        </div>
      </div>
      {showToggle && (
        <button
          className={styles.expandToggle}
          onClick={handleToggle}
          aria-expanded={expanded}
          aria-label={expanded ? t('common.collapse') : t('common.expand')}
          type='button'
        >
          <span aria-hidden='true'>{expanded ? 'Show Less' : 'Show More'}</span>
        </button>
      )}
    </div>
  )
}

export default ExpandableText